import { assigneeMatches, type Assignee } from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../i18n";

/**
 * Pick a person or a crew, by typing their name.
 *
 * A dropdown was fine while the only answer was "one of the three inspectors".
 * Once every member of staff and every crew is a possible answer it is a list
 * of forty, and a rep in a driveway wants to type "pablo" rather than scroll.
 *
 * Staff and crews sit in one list on purpose. Which of the two somebody is
 * matters to the database, not to the rep looking for them — so the kind is
 * shown beside the name and never asked for.
 */
export function AssigneePicker({
  value,
  options,
  placeholder = t("Search staff and crews by name…"),
  onChange,
}: {
  value: Assignee | null;
  options: readonly Assignee[];
  placeholder?: string;
  onChange: (picked: Assignee | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  if (value) {
    return (
      <div className="picked">
        <div>
          <b>{value.name}</b>
          {/* Once somebody is picked their name is enough. Every role a member
              of staff holds ran to two lines under it; Jonathon, 2026-09-29:
              "we don't need to see all of these roles by our names". A crew
              keeps its "Crew", which says who is going rather than what they
              do. The list you pick from still shows roles. */}
          {value.kind === "crew" && (
            <div className="sub">{t(value.detail)}</div>
          )}
        </div>
        <span className="chg" onClick={() => onChange(null)}>
          {t("Remove")}
        </span>
      </div>
    );
  }

  // Everyone, until they start typing. Opening an empty box onto nothing reads
  // as "there is nobody to pick".
  const hits = options.filter((o) => assigneeMatches(o, query)).slice(0, 8);

  return (
    <span style={{ position: "relative", display: "block" }}>
      <input
        placeholder={placeholder}
        autoComplete="off"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && hits.length > 0 && (
        <div className="ac-drop">
          {hits.map((hit) => (
            <div
              key={`${hit.kind}:${hit.id}`}
              className="ac-item"
              onMouseDown={() => {
                onChange(hit);
                setQuery("");
                setOpen(false);
              }}
            >
              <div className="an">{hit.name}</div>
              <div className="aa">{t(hit.detail)}</div>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
